import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useHousingData } from '../hooks/useHousingData';

const Toast = ({ message, type = 'success', onClose }) => (
  <div className="fixed top-6 right-6 z-[70] animate-slide-in">
    <div className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl border ${
      type === 'success'
        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
        : type === 'error'
        ? 'bg-red-50 border-red-200 text-red-800'
        : 'bg-blue-50 border-blue-200 text-blue-800'
    }`}>
      <span className="text-lg">
        {type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}
      </span>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-4 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
    <span className="text-lg">{icon}</span>
    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
  </div>
);

const FormField = ({ label, required, children, hint }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

const EventModal = ({ event, selectedDate, selectedSlot, onSave, onDelete, onClose, onReschedule, onFinalize }) => {
  const { t } = useTranslation();
  const { properties } = useHousingData();
  const propertyInputRef = useRef(null);

  const [formData, setFormData] = useState({
    userName: '',
    phone: '',
    propertyName: '',
    propertyId: '',
    district: '',
    date: '',
    notes: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  // Revisit state
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (propertyInputRef.current && !propertyInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!formData.district) {
      setFilteredProperties([]);
      return;
    }

    const searchLower = propertySearch.toLowerCase();
    let filtered = properties.filter((prop) => {
      const matchesDistrict = prop.district === formData.district || 
                             prop.districtVi === formData.district;
      return matchesDistrict;
    });

    if (searchLower) {
      filtered = filtered.filter((prop) => {
        const matchesSearch = prop.titleVi?.toLowerCase().includes(searchLower) ||
                             prop.title?.toLowerCase().includes(searchLower);
        return matchesSearch;
      });
    }

    setFilteredProperties(filtered.slice(0, 8));
  }, [propertySearch, formData.district, properties]);

  useEffect(() => {
    if (event) {
      setIsEditing(true);
      setFormData({
        userName: event.userName || '',
        phone: event.phone || '',
        propertyName: event.propertyName || '',
        propertyId: event.propertyId || '',
        district: event.district || '',
        date: event.date ? format(new Date(event.date), "yyyy-MM-dd'T'HH:mm") : '',
        notes: event.notes || '',
      });
      if (event.propertyName) {
        setPropertySearch(event.propertyName);
      }
    } else if (selectedDate) {
      setIsEditing(false);
      let defaultDate;
      if (selectedSlot) {
        const dateWithSlot = new Date(selectedDate);
        dateWithSlot.setHours(selectedSlot, 0, 0, 0);
        defaultDate = format(dateWithSlot, "yyyy-MM-dd'T'HH:mm");
      } else {
        defaultDate = format(selectedDate, "yyyy-MM-dd'T'HH:mm");
      }
      setFormData({
        userName: '',
        phone: '',
        propertyName: '',
        propertyId: '',
        district: '',
        date: defaultDate,
        notes: '',
      });
      setPropertySearch('');
    }
  }, [event, selectedDate, selectedSlot]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (name === 'district') {
      setPropertySearch('');
      setFormData((prev) => ({
        ...prev,
        propertyName: '',
        propertyId: '',
      }));
    }
  };

  const handlePropertySelect = (property) => {
    setFormData((prev) => ({
      ...prev,
      propertyName: property.titleVi || property.title,
      propertyId: property.id || '',
    }));
    setPropertySearch(property.titleVi || property.title);
    setShowSuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.userName || !formData.phone || !formData.propertyName || !formData.date) {
      showToast(t('calendar.errorRequiredFields'), 'error');
      return;
    }

    onSave({
      ...formData,
      date: new Date(formData.date).toISOString(),
      propertyLink: formData.propertyId ? `/housing/${formData.propertyId}` : '',
    });
    showToast(isEditing ? '✅ Đã cập nhật sự kiện' : '✅ Đã tạo sự kiện mới');
  };

  const handleRescheduleSubmit = () => {
    if (!rescheduleDate) {
      showToast(t('calendar.errorRequiredFields'), 'error');
      return;
    }
    if (!event) return;

    const oldDateStr = format(new Date(event.date), 'dd/MM/yyyy HH:mm');
    const revisitNote = t('calendar.revisitNote', { date: oldDateStr });

    let combinedNotes = event.notes
      ? `${event.notes}\n\n---\n${revisitNote}`
      : revisitNote;

    if (rescheduleNote.trim()) {
      combinedNotes += `\n\n📝 ${rescheduleNote.trim()}`;
    }

    const newEvent = {
      id: Date.now().toString(),
      userName: event.userName,
      phone: event.phone,
      propertyName: event.propertyName,
      propertyId: event.propertyId,
      district: event.district,
      date: new Date(rescheduleDate).toISOString(),
      notes: combinedNotes,
      propertyLink: event.propertyLink,
      isRevisit: true,
      revisitOfId: event.id,
    };

    onReschedule(newEvent);
    setShowReschedule(false);
    setRescheduleDate('');
    setRescheduleNote('');
    showToast('✅ Đã tạo lịch hẹn xem lại');
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
    showToast('🗑️ Đã xóa sự kiện');
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [confirmingFinalize, setConfirmingFinalize] = useState(false);

  const handleFinalizeClick = () => {
    setShowFinalizeConfirm(true);
  };

  const confirmFinalize = async () => {
    setConfirmingFinalize(true);
    await onFinalize(event);
    setConfirmingFinalize(false);
    setShowFinalizeConfirm(false);
    showToast('✅ Đã đánh dấu hoàn tất');
  };

  const isRevisitEvent = event?.isRevisit;
  const isFinalizedEvent = event?.isFinalized;

  return (
    <>
      {/* Toast notifications */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-0 max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isFinalizedEvent ? 'bg-emerald-100' :
                isRevisitEvent ? 'bg-amber-100' :
                isEditing ? 'bg-primary-100' : 'bg-blue-100'
              }`}>
                {isFinalizedEvent ? '✅' : isRevisitEvent ? '🔁' : isEditing ? '✏️' : '📅'}
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {isEditing ? t('calendar.editEvent') : t('calendar.newEvent')}
                </h2>
                <p className="text-xs text-gray-500">
                  {isFinalizedEvent ? 'Đã hoàn tất' :
                   isRevisitEvent ? 'Lịch hẹn xem lại' :
                   isEditing ? 'Chỉnh sửa thông tin' : 'Tạo lịch hẹn xem phòng mới'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto px-6 py-5 flex-1">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Section: Thông tin khách */}
              <div>
                <SectionTitle icon="👤" title="Thông tin khách" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label={t('calendar.userName')} required>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">👤</span>
                      <input
                        type="text"
                        name="userName"
                        value={formData.userName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm"
                        placeholder={t('calendar.userNamePlaceholder')}
                        required
                      />
                    </div>
                  </FormField>
                  <FormField label={t('calendar.phone')} required>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📞</span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm"
                        placeholder="090 xxx xxxx"
                        required
                      />
                    </div>
                  </FormField>
                </div>
              </div>

              {/* Section: Thời gian */}
              <div>
                <SectionTitle icon="⏰" title="Thời gian" />
                <FormField label={t('calendar.dateTime')} required hint="Chọn ngày và giờ hẹn xem phòng">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📅</span>
                    <input
                      type="datetime-local"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm"
                      required
                    />
                  </div>
                </FormField>
              </div>

              {/* Section: Phòng trọ */}
              <div>
                <SectionTitle icon="🏠" title="Phòng trọ" />
                <div className="space-y-4">
                  <FormField label={t('calendar.district')} required>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📍</span>
                      <select
                        name="district"
                        value={formData.district}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm bg-white appearance-none"
                        required
                      >
                        <option value="">{t('calendar.selectDistrict')}</option>
                        {properties.length > 0 ? (
                          [...new Set(properties.map(p => p.district || p.districtVi))].sort().map((district) => (
                            <option key={district} value={district}>{district}</option>
                          ))
                        ) : (
                          ['Quận 1', 'Quận 2', 'Quận 3', 'Quận 4', 'Quận 5', 'Quận 6', 'Quận 7', 'Quận 8', 'Quận 9', 'Quận 10', 'Quận 11', 'Quận 12', 'Bình Thạnh', 'Thủ Đức', 'Gò Vấp', 'Tân Bình', 'Tân Phú', 'Phú Nhuận', 'Bình Chánh', 'Củ Chi', 'Hóc Môn', 'Nhà Bè', 'Cần Giờ'].map((district) => (
                            <option key={district} value={district}>{district}</option>
                          ))
                        )}
                      </select>
                    </div>
                  </FormField>

                  <FormField label={t('calendar.propertyName')} required>
                    <div ref={propertyInputRef} className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">🔍</span>
                      <input
                        type="text"
                        value={propertySearch}
                        onChange={(e) => {
                          setPropertySearch(e.target.value);
                          setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm"
                        placeholder={t('calendar.propertyNamePlaceholder')}
                        required
                      />
                      
                      {/* Suggestions dropdown */}
                      {showSuggestions && filteredProperties.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                          {filteredProperties.map((property, index) => (
                            <div
                              key={index}
                              onClick={() => handlePropertySelect(property)}
                              className="flex items-start gap-3 px-4 py-3 hover:bg-primary-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-sm">🏠</span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 text-sm truncate">
                                  {property.titleVi || property.title}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                  <span>📍 {property.districtVi || property.district}</span>
                                  <span>•</span>
                                  <span>💰 {property.price?.toLocaleString('vi-VN')}₫</span>
                                </div>
                              </div>
                              {formData.propertyId === property.id && (
                                <span className="text-primary-600 flex-shrink-0">✓</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </FormField>

                  {/* Property detail link */}
                  {formData.propertyName && filteredProperties.find(p => (p.titleVi || p.title) === formData.propertyName) && (
                    <div className="pl-10">
                      <a
                        href={`/housing/${filteredProperties.find(p => (p.titleVi || p.title) === formData.propertyName)?.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        🔗 {t('calendar.viewPropertyDetails')}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Section: Ghi chú */}
              <div>
                <SectionTitle icon="📝" title="Ghi chú" />
                <FormField label={t('calendar.notes')}>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm resize-none"
                    placeholder={t('calendar.notesPlaceholder')}
                  />
                </FormField>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-semibold text-sm shadow-sm hover:shadow-md active:scale-[0.98]"
                >
                  {isEditing ? '💾 ' + t('calendar.update') : '➕ ' + t('calendar.create')}
                </button>
                {isEditing && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="flex-1 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 transition-colors font-medium text-sm"
                    >
                      🗑️ {t('calendar.delete')}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm text-gray-600"
                    >
                      ✕ {t('calendar.cancel')}
                    </button>
                  </div>
                )}
              </div>
            </form>

            {/* Mark as Done — for non-finalized events */}
            {isEditing && onFinalize && !isFinalizedEvent && (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleFinalizeClick}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors font-medium text-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('calendar.finalizedAction')}
                </button>
              </div>
            )}

            {/* Revisit & Reschedule — for non-finalized events */}
            {isEditing && onReschedule && !isFinalizedEvent && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                {!showReschedule ? (
                  <button
                    type="button"
                    onClick={() => {
                      const nextWeek = new Date(event.date);
                      nextWeek.setDate(nextWeek.getDate() + 7);
                      setRescheduleDate(format(nextWeek, "yyyy-MM-dd'T'HH:mm"));
                      setRescheduleNote('');
                      setShowReschedule(true);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors font-medium text-sm"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('calendar.revisitAction')}
                  </button>
                ) : (
                  <div className="space-y-4 p-4 bg-amber-50/50 rounded-xl border border-amber-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {t('calendar.revisitAction')}
                      </h4>
                      <button
                        type="button"
                        onClick={() => setShowReschedule(false)}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-amber-100 text-amber-500"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-amber-700 mb-1.5">
                        {t('calendar.revisitNewDate')} *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500">📅</span>
                        <input
                          type="datetime-local"
                          value={rescheduleDate}
                          onChange={(e) => setRescheduleDate(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-sm bg-white"
                          required
                        />
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-100">
                      {t('calendar.revisitNote', { date: format(new Date(event.date), 'dd/MM/yyyy HH:mm') })}
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-amber-700 mb-1.5">
                        📝 {t('calendar.notes')}
                      </label>
                      <textarea
                        value={rescheduleNote}
                        onChange={(e) => setRescheduleNote(e.target.value)}
                        rows="2"
                        className="w-full px-4 py-2.5 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-sm resize-none bg-white"
                        placeholder={t('calendar.notesPlaceholder')}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleRescheduleSubmit}
                        className="flex-1 bg-amber-600 text-white py-2.5 px-4 rounded-xl hover:bg-amber-700 transition-colors font-medium text-sm shadow-sm"
                      >
                        {t('calendar.revisitAction')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowReschedule(false)}
                        className="px-4 py-2.5 border border-amber-300 rounded-xl hover:bg-amber-50 transition-colors font-medium text-sm text-amber-700"
                      >
                        {t('calendar.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show revisit notes history */}
            {isRevisitEvent && event.notes && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">📋 Lịch sử xem lại</h4>
                <div className="text-xs text-amber-800 whitespace-pre-line leading-relaxed">{event.notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Finalize confirmation modal */}
      {showFinalizeConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('calendar.finalizedLabel')}</h3>
              <p className="text-sm text-gray-500 mb-6">{t('calendar.finalizedSubmitted')}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFinalizeConfirm(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm text-gray-600"
                >
                  {t('calendar.cancel')}
                </button>
                <button
                  onClick={confirmFinalize}
                  disabled={confirmingFinalize}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-60 transition-colors font-medium text-sm shadow-sm"
                >
                  {confirmingFinalize ? '⏳ Đang xử lý...' : '✅ ' + t('calendar.finalizedAction')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('calendar.confirmDelete')}</h3>
              <p className="text-sm text-gray-500 mb-6">Hành động này không thể hoàn tác.</p>
              <div className="flex gap-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm text-gray-600"
                >
                  {t('calendar.cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm shadow-sm"
                >
                  {t('calendar.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventModal;
